import { Trans, withI18n } from '@lingui/react'
import classNames from 'clsx'
import { bool, number, oneOf, shape } from 'prop-types'
import { compose } from 'redux'

import {
  ANALYTICS_ATTR_IMAGE,
  ANALYTICS_ATTR_TITLE,
  DEFAULT_WIDGETS,
  TYPE_PDP_SLIDER_TOP,
} from 'shared/consts/crosssellWidgets'
import { PRESCRIPTION_TYPE_NO_PRESCRIPTION } from 'shared/consts/productTemplates'
import { isMarketplace } from 'shared/experiments/utils/featureFlags'
import { isRebrandingITActive } from 'shared/experiments/utils/rebranding'
import getProductUrl from 'shared/utils/getProductUrl'
import Button from 'views/components/atoms/Button'
import ButtonAsLink from 'views/components/molecules/ButtonAsLink'
import ButtonImage from 'views/components/molecules/ButtonImage'
import ImageWithDiscountBadge from 'views/components/molecules/ImageWithDiscountBadge'
import StarRating from 'views/components/molecules/StarRating'
import SearchProductLabel from 'views/components/organisms/FilteredProductList/SearchProductListItem/SearchProductLabel'
import { SELLER_MODAL_ID } from 'views/components/organisms/SellerModal/names'
import {
  getAmount,
  resolveLazyLoad,
} from 'views/components/organisms/SliderHorizontal/ProductSliderItem/helpers'
import { getSellerModalHook } from 'views/containers/browser/SellerModalContainer/clientsideHook'
import { shouldRenderRedPointsBadge } from 'views/containers/crosssell/CrosssellSliderTop/helpers'
import withMountPoints from 'views/enhancers/withMountPoints'
import withShopAndRuntimeConfig from 'views/enhancers/withShopAndRuntimeConfig'
import {
  i18nType,
  mountPointsType,
  ProductType,
  publicRuntimeConfigType,
} from 'views/utils/propTypes'

import PriceInfo from './PriceInfo'

import 'views/components/organisms/SliderHorizontal/ProductSliderItem/ProductSliderItem.scss'
import './ProductSliderItem.scss'

const ProductSliderItem = ({
  asTopSlider,
  goToProductButton,
  headerTag: HeaderComponent,
  i18n,
  index,
  lazyLoad,
  mountPoints,
  noBorder,
  noSlider,
  product,
  redirectToCart,
  showBuyButton,
  sparse,
  srcHeight,
  srcWidth,
  thumbnail,
  whiteImageBg,
  wid,
  publicRuntimeConfig: {
    publicConfig: { deviceClass, tenant },
    pageProperties: { experiments },
  },
}) => {
  const {
    id,
    canonicalURL,
    thumbnailURL,
    imageUrl,
    name,
    rating,
    sellerId,
    sellerName,
    numberOfRatings,
    multiplierRP,
    darreichung: serving,
    offersCount,
    offerId,
  } = product
  const className = classNames('o-ProductSliderItem', {
    'o-ProductSliderItem--no-border': noBorder,
    'o-ProductSliderItem--white-img-bg': whiteImageBg,
    'o-ProductSliderItem--top': asTopSlider,
    'o-ProductSliderItem--default': !asTopSlider,
  })
  const amount = getAmount(product)
  const productUrl = getProductUrl(canonicalURL)
  const sellerIdSAE = 'shop-apotheke'

  const shouldRenderRPLabel = shouldRenderRedPointsBadge(product)
  const shouldShowAbPrice =
    offersCount &&
    offersCount > 1 &&
    isMarketplace(experiments, tenant) &&
    wid === DEFAULT_WIDGETS[tenant][TYPE_PDP_SLIDER_TOP]
  const shouldShowSellerBox =
    showBuyButton &&
    sellerId &&
    sellerId !== sellerIdSAE &&
    offersCount === 1 &&
    isMarketplace(experiments, tenant)

  const isRebranding = isRebrandingITActive(experiments)

  const CTA = (
    <div className="u-margin-small--top o-ProductSliderItem__add-to-cart__wrapper">
      <Button
        href={mountPoints.getAddToCart({
          cartAction: 'add',
          eventName: 'addToCart_crossSell',
          eventType: 'conversion',
          offer: offerId,
          prescriptionType: PRESCRIPTION_TYPE_NO_PRESCRIPTION,
          product: product.id,
          quantity: 1,
          redirectToCart,
        })}
        name={`product-slider-${product.id}-add-to-cart-button`}
        style="buttonGhost"
        className="o-ProductSliderItem__add-to-cart u-no-margin"
      >
        <div className="a-h3 u-font-weight--normal u-no-margin">
          <Trans id="general.button.label.addToCart" />
        </div>
      </Button>
    </div>
  )

  return (
    <article className={className} data-product-id={id}>
      <div className="o-ProductSliderItem__main">
        {shouldRenderRPLabel && (
          <div className="o-SearchProductListItem__label-item">
            <SearchProductLabel
              label={`${parseFloat(multiplierRP)}x ${i18n._(
                'general.label.redPoints'
              )}`}
              bgcolor="background"
              bordercolor="red"
              color="red"
              dataQaId={multiplierRP}
            />
          </div>
        )}
        <div
          className="o-ProductSliderItem__image"
          data-clientside-hook="slider__image"
        >
          <ButtonImage
            name={`product-slider-${id}-image-button`}
            href={productUrl}
            noBorder
          >
            <ImageWithDiscountBadge
              imageUrl={thumbnailURL || imageUrl}
              srcWidth={srcWidth}
              srcHeight={srcHeight}
              alt={name}
              thumbnail={thumbnail}
              lazyLoad={resolveLazyLoad(deviceClass, index, lazyLoad)}
              imageAttrs={{
                'data-analytics': ANALYTICS_ATTR_IMAGE,
              }}
            />
          </ButtonImage>
        </div>
        <div
          data-clientside-hook="stretchVertically"
          data-stretch-group="card-content"
          className={classNames({
            'u-padding-x-small': sparse,
          })}
        >
          <div className="o-ProductSliderItem__subHeader">
            <HeaderComponent
              className={classNames(
                'o-ProductSliderItem__subHeaderElement u-no-margin--bottom u-padding-small--top u-padding-small--sides',
                {
                  'a-p8': !asTopSlider && !sparse,
                  'a-p': asTopSlider,
                  'u-font-weight--bold': HeaderComponent === 'h2',
                }
              )}
            >
              <ButtonAsLink
                href={productUrl}
                name={`product-slider-${id}-link-button`}
                className={classNames('o-ProductSliderItem__link', {
                  'a-truncate-with-ellipsis': asTopSlider,
                })}
                data-analytics={ANALYTICS_ATTR_TITLE}
              >
                {name}
              </ButtonAsLink>
            </HeaderComponent>
            <div className="o-ProductSliderItem__servingDetails a-truncate-with-ellipsis u-text-align--left u-padding-small--sides">
              {amount}
              {Boolean(amount && serving) && ' | '}
              {serving}
            </div>
            <div className="u-text-align--left u-padding-small--sides">
              <StarRating
                rating={rating}
                ratingCount={numberOfRatings}
                iconSize="x-small"
                invisible={!(rating > 0 || numberOfRatings > 0)}
                href={mountPoints.getProductReviews(productUrl)}
                hasSuperScript
              />
            </div>
          </div>
          <div className="o-ProductSliderItem__pricesContainer u-text-align--left u-padding-small--sides u-padding-xx-small--top">
            <PriceInfo
              product={product}
              shouldDisplayAbPrice={shouldShowAbPrice}
            />
          </div>
          {showBuyButton && noSlider && isRebranding && CTA}
        </div>
      </div>
      {(!noSlider || !isRebranding) && showBuyButton && CTA}
      {goToProductButton && (
        <div className="u-margin-small--top">
          <Button
            href={getProductUrl(canonicalURL)}
            name={`product-highlights-cta-${product.id}`}
            style="buttonGhost"
            className="u-no-margin"
          >
            <div className="a-h3 u-font-weight--normal u-no-margin">
              <Trans id="general.button.label.productLink" />
            </div>
          </Button>
        </div>
      )}
      {shouldShowSellerBox && (
        <div className="a-box u-padding-small u-text-align--left">
          <div className="a-p8 cross-sell-widget-text">
            <Trans id="message.soldAndShipped.partnerCrossSell" />
            <ButtonAsLink
              data-a11y-dialog-show={SELLER_MODAL_ID}
              href={`#${SELLER_MODAL_ID}`}
              data-clientside-hook={getSellerModalHook(sellerId)}
              className="partner-link-text u-padding-xx-small--left"
              data-qa-id="seller-name"
              data-target="seller-box"
            >
              {sellerName}
            </ButtonAsLink>
          </div>
        </div>
      )}
    </article>
  )
}

ProductSliderItem.propTypes = {
  i18n: i18nType.isRequired,
  mountPoints: mountPointsType.isRequired,
  publicRuntimeConfig: shape(publicRuntimeConfigType).isRequired,
  product: shape(ProductType).isRequired,
  asTopSlider: bool,
  goToProductButton: bool,
  headerTag: oneOf(['h2', 'h3']),
  index: number,
  lazyLoad: bool,
  noBorder: bool,
  noSlider: bool,
  redirectToCart: bool,
  showBuyButton: bool,
  sparse: bool,
  srcHeight: number,
  srcWidth: number,
  thumbnail: bool,
  whiteImageBg: bool,
  wid: number,
}

ProductSliderItem.defaultProps = {
  asTopSlider: false,
  goToProductButton: false,
  headerTag: 'h3',
  index: -1,
  lazyLoad: false,
  noBorder: false,
  noSlider: false,
  redirectToCart: false,
  showBuyButton: false,
  sparse: false,
  srcHeight: 250,
  srcWidth: 250,
  thumbnail: false,
  whiteImageBg: false,
  wid: null,
}

export default compose(
  withI18n(),
  withMountPoints,
  withShopAndRuntimeConfig
)(ProductSliderItem)
